import { Injectable } from '@angular/core';
import { Observable, combineLatest, EMPTY } from 'rxjs';
import { map, shareReplay, filter, tap, switchMap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';

import { FindingStore, AssetStore, AnalystStore, MicroLayoutStore, RoiStore } from '../../stores';
import { MicroRoiFeatureStore } from '../micro-roi-feature';
import { ProtocolScope, ProtocolStore } from '@telespot/protocols/ui';
import { Analyst, FindingItem } from '@analysis/domain';
import { StepTask } from '@telespot/domain';
import Parse from 'parse';

@Injectable()
export class MicroFindingEffects {
  // --- Selectors ---

  public readonly activeAssetFindings$ = combineLatest([
    this.assetStore.selectedAsset$,
    this.analystStore.selectedAnalyst$,
    this.findingStore.findingsWithMetadata$,
  ]).pipe(
    map(([selectedAsset, selectedAnalyst, findings]) => {
      if (!selectedAsset || !selectedAnalyst || !findings) return [];
      const assetId = selectedAsset.id;
      const analystId = selectedAnalyst.id;
      const analystEntity = selectedAnalyst.type;
      return findings.filter(
        (finding: any) =>
          finding.assetId === assetId && finding.creatorId === analystId && finding.creatorEntity === analystEntity
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly activeSampleFindings$ = combineLatest([
    this.analystStore.selectedAnalyst$,
    this.findingStore.sampleFindingsWithMetadata$,
  ]).pipe(
    map(([selectedAnalyst, sampleFindings]) => {
      if (!selectedAnalyst || !sampleFindings) return [];
      const analystId = selectedAnalyst.id;
      const analystEntity = selectedAnalyst.type;
      return sampleFindings.filter(
        (finding: any) => finding.creatorId === analystId && finding.creatorEntity === analystEntity
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly activeFindings$ = combineLatest([
    this.protocolStore.scope$,
    this.activeAssetFindings$,
    this.activeSampleFindings$,
  ]).pipe(
    map(([scope, assetFindings, sampleFindings]) => (scope === ProtocolScope.Asset ? assetFindings : sampleFindings)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly activeRoiFindings$ = combineLatest([
    this.assetStore.selectedAsset$,
    this.analystStore.selectedAnalyst$,
    this.roiStore.roiFindings$,
  ]).pipe(
    map(([selectedAsset, selectedAnalyst, roiFindings]) => {
      if (!selectedAsset || !selectedAnalyst || !roiFindings) return [];
      const assetId = selectedAsset.id;
      const analystId = selectedAnalyst.id;
      return Array.from(roiFindings.values()).filter(
        (roiFinding) => roiFinding.creatorId === analystId && roiFinding.assetId === assetId
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly canEdit$ = combineLatest([
    this.analystStore.selectedAnalyst$,
    this.microLayout.editingEnabled$,
  ]).pipe(
    map(([selectedAnalyst, editingEnabled]) => {
      const isReviewingOtherAnalyst = selectedAnalyst ? selectedAnalyst.id !== Parse.User.current()?.id : false;
      return !isReviewingOtherAnalyst || editingEnabled;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly findingStore: FindingStore,
    private readonly microRoiFeatureStore: MicroRoiFeatureStore,
    private readonly roiStore: RoiStore,
    private readonly assetStore: AssetStore,
    private readonly analystStore: AnalystStore,
    private readonly protocolStore: ProtocolStore,
    private readonly microLayout: MicroLayoutStore
  ) {}

  // --- Effects ---

  public readonly fetchAssetFindings = this.findingStore.effect(
    (action$: Observable<{ assetId: string; historyId: string; sampleId: string }>) =>
      action$.pipe(
        concatLatestFrom(() => [this.protocolStore.steps$, this.analystStore.selectedAnalyst$]),
        tap(([{ sampleId, assetId, historyId }, steps, selectedAnalyst]) => {
          const analystId = historyId ? selectedAnalyst?.id : Parse.User.current()?.id;
          const analystEntity = historyId ? selectedAnalyst?.type : 'user';

          if (!analystId || !analystEntity) return;

          this.microRoiFeatureStore.loadAssetRois({ assetId, sampleId, analystId, analystEntity });
          this.findingStore.fetchAssetFindings({
            assetId,
            sampleId,
            analystId,
            analystEntity,
            pipelineStepIds: steps.map((step) => step.id),
          });
        }),
        switchMap(() => EMPTY)
      )
  );

  public readonly fetchSampleFindings = this.findingStore.effect(
    (action$: Observable<{ analyst: Analyst; sampleId: string }>) =>
      action$.pipe(
        concatLatestFrom(() => [this.protocolStore.steps$, this.findingStore.sampleFindings$]),
        tap(([{ sampleId, analyst }, steps, findings]) => {
          const hasCachedFindings = findings.some(
            (finding: FindingItem) => finding.creatorId === analyst.id && finding.creatorEntity === analyst.type
          );

          if (hasCachedFindings) return;

          this.findingStore.fetchSampleFindings({
            sampleId,
            pipelineStepIds: steps.map((s) => s.id),
            analystEntity: analyst.type,
            analystId: analyst.id,
          });
        }),
        switchMap(() => EMPTY)
      )
  );

  public readonly updateFinding = this.findingStore.effect(
    (action$: Observable<{ id: string; taskType: 'text' | 'classification'; value: string | string[] }>) =>
      action$.pipe(
        concatLatestFrom(() => this.canEdit$),
        filter(([, canEdit]) => canEdit),
        tap(([{ id, taskType, value }]) => {
          this.findingStore.updateFinding({ findingId: id, data: value, type: taskType });
        }),
        switchMap(() => EMPTY)
      )
  );

  public readonly createFinding = this.findingStore.effect(
    (action$: Observable<{ taskType: StepTask; value: string | string[]; pipelineId: string; taskId: string }>) =>
      action$.pipe(
        concatLatestFrom(() => [
          this.assetStore.selectedAsset$,
          this.analystStore.selectedAnalyst$,
          this.activeFindings$,
          this.protocolStore.scope$,
        ]),
        filter(([, , selectedAnalyst]) => !!selectedAnalyst?.id),
        tap(([{ taskType, value, pipelineId, taskId }, selectedAsset, selectedAnalyst, activeFindings, scope]) => {
          const assetId = scope === ProtocolScope.Asset ? selectedAsset?.id : undefined;
          const analystId = selectedAnalyst.id;
          const analystEntity = selectedAnalyst.type;

          const pipelineFindings = activeFindings.filter((finding: any) => finding.pipelineId === pipelineId);

          this.findingStore.createFinding({
            assetId,
            creatorId: analystId,
            creatorEntity: analystEntity,
            pipelineId,
            taskId,
            taskType,
            value,
            existingFindings: pipelineFindings as any,
          });
        }),
        switchMap(() => EMPTY)
      )
  );
}
